import RcSlider, { Handle } from 'rc-slider';
import RcTooltip from 'rc-tooltip';
import type { IFieldProps } from '../elements/Field';
import type { FormContextProps } from '../elements/formContext';
import { isBrowserInternetExplorer } from '../../../common/utils/isBrowserInternetExplorer';
import { Feedback } from '../elements/common';
import { Field } from '../elements/Field';
import { FormContextConsumer } from '../elements/formContext';

interface ISteppedSliderInputProps<T extends string> {
    automation?: string;
    disabled?: boolean;
    error?: string;
    marks?: IMarks;
    onChange: (value: T) => void;
    onDoValidate?: () => void;
    options: T[];
    showLabels?: boolean;
    value: T;
}

interface IMarks {
    [number: number]: string;
}

const showTooltips = !isBrowserInternetExplorer();

export function SteppedSliderInput<T extends string>(props: FormContextProps<ISteppedSliderInputProps<T>>) {
    return (
        <FormContextConsumer {...props}>
            {(properties) => {
                if (properties.options.length < 2) {
                    throw new Error('SteppedSlider requires at least 2 options');
                }

                const valueScore = properties.options.findIndex((option: string) => {
                    return option === properties.value;
                });

                return (
                    <>
                        <div className="stepped-slider">
                            <div className={`rc-slider-wrapper ${properties.showLabels ? 'hasLabels' : 'noLabels'}`}>
                                {properties.showLabels ? (
                                    <span className="rc-state">{properties.options[0]}</span>
                                ) : null}
                                <RcSlider
                                    className={props.automation ? 'automation-' + props.automation : ''}
                                    disabled={properties.disabled}
                                    dots
                                    dotStyle={{
                                        backgroundColor: '#ECECE6',
                                        borderColor: '#ECECE6',
                                        height: 26,
                                        width: 2,
                                        borderRadius: 26,
                                        border: 0,
                                        top: -8,
                                    }}
                                    handle={
                                        showTooltips
                                            ? (handleValue: any) => {
                                                  const { value, dragging, index, ...restValues } = handleValue;

                                                  return (
                                                      <RcTooltip
                                                          key={index}
                                                          overlay={properties.options[value]}
                                                          overlayStyle={{ zIndex: 9999 }}
                                                          placement="top"
                                                          prefixCls="rc-slider-tooltip"
                                                          visible={dragging}
                                                      >
                                                          <Handle
                                                              value={value}
                                                              {...restValues}
                                                          />
                                                      </RcTooltip>
                                                  );
                                              }
                                            : undefined
                                    }
                                    handleStyle={{
                                        backgroundColor: '#003BD6',
                                        width: 30,
                                        height: 30,
                                        border: 0,
                                        marginTop: -8,
                                    }}
                                    marks={showTooltips ? properties.marks : { ...properties.options }}
                                    max={properties.options.length - 1}
                                    min={0}
                                    onChange={async (value: any) => {
                                        await properties.onChange(properties.options[value]);
                                        if (properties.onDoValidate) {
                                            await properties.onDoValidate();
                                        }
                                    }}
                                    railStyle={{ backgroundColor: '#ECECE6', height: 8 }}
                                    trackStyle={{ backgroundColor: '#ECECE6', height: 8 }}
                                    value={valueScore}
                                />
                                {properties.showLabels ? (
                                    <span className="rc-state">
                                        {properties.options[properties.options.length - 1]}
                                    </span>
                                ) : null}
                            </div>
                        </div>
                        <Feedback
                            error={properties.error}
                            key="feedback"
                        />
                    </>
                );
            }}
        </FormContextConsumer>
    );
}

export function SteppedSliderField<T extends string>(
    props: FormContextProps<ISteppedSliderInputProps<T> & IFieldProps>
) {
    return (
        <Field {...props}>
            <SteppedSliderInput<T> {...props} />
        </Field>
    );
}
